import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { useSimulator } from "../Simulator/SimulatorStore";

Chart.register(...registerables);

export default function BottleneckChart() {
  const { processes, global } = useSimulator();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const inputsZero =
    global.dailyLeadVolume === 0 &&
    global.leadQuality === 0 &&
    global.baseConversionRate === 0 &&
    global.cycleTimeImpact === 0;

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    const labels = processes.map((p) => p.base.name);
    const values = inputsZero
      ? processes.map(() => 0)
      : processes.map((p) => Math.round(p.derived.utilizationPct));

    // Match original SVG gradient and stroke
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, "#60A5FA");
    gradient.addColorStop(1, "#1E40AF");

    if (!chartRef.current) {
      chartRef.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Average Utilization (%)",
              data: values,
              backgroundColor: gradient,
              borderColor: "#1E3A8A",
              borderWidth: 1,
              hoverBorderColor: "#1E3A8A",
              borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: "#666", font: { size: 12 } },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              suggestedMax: 100,
              ticks: { stepSize: 20, color: "#666", font: { size: 12 } },
              grid: { color: "#e5e7eb" },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `Average Utilization: ${ctx.parsed.y}%`,
              },
            },
          },
        },
      });
    } else {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = values as any;
      chartRef.current.update();
    }

  }, [inputsZero, processes.map((p) => p.derived.utilizationPct).join("|"), processes.map((p) => p.base.name).join("|")]);

  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Bottleneck Analysis</h4>
      <div className="relative" style={{ height: 300 }}>
        <canvas ref={canvasRef} />
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#3b82f6" }} />
        <span>Average Utilization: % of capacity typically used</span>
      </div>
    </div>
  );
}
